// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Agents terminals. The person's own terminals on their machine, live and typed into from
// Konvo (the sessions below). The read-only Terminals panel of konvo-agents 0.38 and older is gone.

import {z} from 'zod';

export const AgentTerminalStateSchema = z.object({
	/** starting | running | idle | exited | gone (stopped being there). */
	status: z.string().max(20),
	exitCode: z.number().int().nullable().optional(),
});
export type AgentTerminalState = z.infer<typeof AgentTerminalStateSchema>;

/**
 * Bridge event of konvo-agents 0.38 and older (the read-only panel's output pushes): still accepted,
 * so an old bridge's batch isn't refused, and dropped.
 */
export const AgentLegacyTerminalEventSchema = z.object({type: z.literal('terminal')});

// ---------------------------------------------------------------------------
// Terminal sessions (can.terminalSessions): the person's own terminals, live and typed into from
// Konvo. The app and the machine talk over one WebSocket each, joined by Konvo's relay
// (konvo_api agents/AgentsTerminalSessions.ts), the same way as the Agents browser: the app asks for
// a one-use token, connects AGENT_TERMINAL_VIEW_PATH with it (subprotocol
// AGENT_TERMINAL_SUBPROTOCOL plus `k.<token>`), and the machine dials AGENT_TERMINAL_TUNNEL_PATH.
// Frames are JSON text; Konvo passes them on and keeps nothing. Owner only.
// ---------------------------------------------------------------------------

export const AGENT_TERMINAL_VIEW_PATH = '/konvo/agents/terminals/view';
export const AGENT_TERMINAL_TUNNEL_PATH = '/konvo/agents/terminals/tunnel';
/** The WebSocket subprotocol the app asks for; the token rides beside it as `k.<token>`. */
export const AGENT_TERMINAL_SUBPROTOCOL = 'konvo-terminal.v1';
export const AGENT_TERMINAL_TOKEN_PROTOCOL_PREFIX = 'k.';
/** Terminals opened from Konvo have ids `konvo-<n>`: Konvo sizes these (and only these). */
export const AGENT_TERMINAL_OWN_PREFIX = 'konvo-';
/** One input frame carries at most this many characters (T3 writes at most 64 KiB at once). */
export const AGENT_TERMINAL_INPUT_MAX = 16 * 1024;
/** A frame on the relay is at most this big. */
export const AGENT_TERMINAL_FRAME_MAX = 512 * 1024;
/** Terminals one chat may have open from Konvo at once. */
export const AGENT_TERMINAL_OWN_MAX = 8;

/** POST /konvo/agents/machines/:id/terminals/session (owner): a token for the relay, good once for a minute. */
export const AgentTerminalSessionSchema = z.object({
	session: z.string(),
	token: z.string(),
	path: z.string(),
	protocol: z.string(),
	expiresAt: z.string(),
});
export type AgentTerminalSession = z.infer<typeof AgentTerminalSessionSchema>;

/** Op `terminalTunnel`: the machine dials the relay for this session. */
export const AgentTerminalTunnelArgsSchema = z.object({
	session: z.string().min(1).max(64),
	token: z.string().min(1).max(200),
});
export const AgentTerminalTunnelDataSchema = z.object({connected: z.literal(true)});

/** A terminal of the thread as the live panel lists it. */
export interface AgentLiveTerminal {
	id: string;
	name: string;
	label: string;
	cwd?: string;
	status: 'starting' | 'running' | 'idle' | 'exited' | 'error';
	exitCode?: number | null;
	updatedAt: string;
	/** Opened from Konvo (id `konvo-<n>`): Konvo sizes it. Others were opened in T3 and keep their size. */
	own: boolean;
}

export interface AgentTerminalSize {
	cols: number;
	rows: number;
}

/**
 * App → machine. `req` ties an answer to its request. Input carries `seq`, counting up per terminal
 * and app (`cid` from hello): after a reconnect the app sends what wasn't acked again and the
 * machine skips what it already wrote.
 */
export type AgentTerminalClientFrame =
	| {t: 'hello'; v: 1; cid: string}
	| {t: 'follow'; threadId: string}
	| {t: 'open'; req: string; threadId: string; cols: number; rows: number}
	| {t: 'attach'; threadId: string; id: string}
	| {t: 'detach'; threadId: string; id: string}
	| {t: 'in'; threadId: string; id: string; seq: number; data: string}
	| {t: 'resize'; threadId: string; id: string; cols: number; rows: number}
	| {t: 'close'; req: string; threadId: string; id: string};

/** Machine → app (and `relay` from Konvo itself once the machine is in). */
export type AgentTerminalServerFrame =
	| {t: 'relay'; state: 'ready'}
	| {t: 'hello'; v: 1; ownMax: number}
	| {t: 'list'; threadId: string; terminals: Array<AgentLiveTerminal>}
	| {t: 'opened'; req: string; threadId: string; id: string}
	| {t: 'closed'; req: string; threadId: string; id: string}
	| {
			t: 'snap';
			threadId: string;
			id: string;
			data: string;
			state: AgentTerminalState;
			/** The terminal's own size, when the machine can tell (terminals opened in T3). */
			size?: AgentTerminalSize;
	  }
	| {t: 'out'; threadId: string; id: string; data: string}
	| {t: 'state'; threadId: string; id: string; state: AgentTerminalState}
	| {t: 'size'; threadId: string; id: string; size: AgentTerminalSize}
	| {t: 'ack'; threadId: string; id: string; seq: number}
	| {
			t: 'error';
			code: string;
			message: string;
			req?: string;
			threadId?: string;
			id?: string;
			seq?: number;
	  };
